import { useState, useEffect } from "react";
import {
  GetResultsDto,
  Result,
  ResultPostPutDto,
  ResultPostPutStartnumberDto,
} from "./api/Dtos";
import { DELETE, GET, PUT } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  HStack,
  Input,
  Select,
  Text,
  VStack,
  Button,
  Checkbox,
} from "@chakra-ui/react";

import { FastTable, isNumber } from "./Table/FastTable";
import {
  dateTimeInMsToString,
  dateTimeInMsToStringWithMs,
  timeInMsToStringUTC,
  validDateTimeStr,
  dateTimeStringToMs,
  dateTimeInMsToTimeString,
  dateTimeInMsToTimeStringWithMs,
} from "./Services/ResultService";
import AddResultModal from "./Modals/AddResultModal";

const modifyFields = ["goalTime"] as const;
const modifyDisplayLabels = ["Tid i mål"];
const disableModifying = false;

const fixedFields = [
  "firstname",
  "lastname",
  "birthYear",
  "startnumber",
  "disciplineName",
  "result",
];

const fixedDisplayLabels = [
  "Förnamn",
  "Efternamn",
  "Född",
  "Startnummer",
  "Klass",
  "Löptid",
  "Starttid",
];

type DisplayResult = Result & {
  result: string;
  resultInMs: number | null;
  goalTime: string;
  kontrollerad: boolean;
};

export function FinishList() {
  const globalContext = useGlobalContext();
  const [results, setResults] = useState<Array<DisplayResult>>([]);
  const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
  const [searchStartnrOrName, setSearchStartnrOrName] = useState<
    string | undefined
  >(undefined);
  const [filteredResults, setFilteredResults] = useState<Array<DisplayResult>>(
    []
  );
  const [searchMatches, setSearchMatches] = useState<Array<DisplayResult>>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);
  const [focusedResult, setFocusedResult] = useState<DisplayResult | undefined>(
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchResults();
  }, []);

  // Get results from backend
  const fetchResults = async () => {
    if (globalContext.competition) {
      const response: GetResultsDto = await GET.allReportableResults(
        globalContext.competition.tag,
        globalContext.competition.startgroups
      );

      const displayResults: Array<DisplayResult> = response.results
        .map((r) => {
          const resultInMs =
            r.startTime && r.finishTime
              ? r.finishTime - r.startTime
              : undefined;
          return {
            ...r,
            resultInMs: resultInMs,
            result: resultInMs ? timeInMsToStringUTC(resultInMs) : "-",
            goalTime: r.finishTime ? dateTimeInMsToStringWithMs(r.finishTime) : "-",
          } as DisplayResult;
        })
        .filter((r) => r.resultInMs != null)
        .sort((r1, r2) => (r1.resultInMs || 0) - (r2.resultInMs || 0));

      setResults(displayResults);
      setFilteredResults(displayResults);
    }
  };

  // Apply class filtering and scroll-target selection
  useEffect(() => {
    let newResultsArray = [...results];
    if (filterClass) {
      newResultsArray = newResultsArray.filter(
        (r) => r.disciplineName === filterClass
      );
    }

    const sortedResults = newResultsArray.sort(
      (r1, r2) => (r1.finishTime ?? 0) - (r2.finishTime ?? 0)
    );

    const normalizedSearch = searchStartnrOrName?.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
      setSearchMatches([]);
      setCurrentMatchIndex(0);
      setFocusedResult(undefined);
    } else {
      const matches = isNumber(normalizedSearch)
        ? sortedResults.filter((r) =>
            `${r.startnumber}`.startsWith(normalizedSearch)
          )
        : sortedResults.filter((r) =>
            `${r.firstname} ${r.lastname}`
              .toLocaleLowerCase()
              .includes(normalizedSearch)
          );

      setSearchMatches(matches);
      setCurrentMatchIndex((oldIndex) => {
        if (matches.length === 0) {
          setFocusedResult(undefined);
          return 0;
        }

        const boundedIndex = Math.min(oldIndex, matches.length - 1);
        setFocusedResult(matches[boundedIndex]);
        return boundedIndex;
      });
    }

    setFilteredResults(sortedResults);
  }, [results, filterClass, searchStartnrOrName]);

  useEffect(() => {
    if (searchMatches.length === 0) {
      return;
    }

    setFocusedResult(searchMatches[currentMatchIndex]);
  }, [currentMatchIndex, searchMatches]);

  const addResultPostPut = async (
    newManAddResult: ResultPostPutStartnumberDto
  ) => {
    console.info(
      "add: " +
        newManAddResult.finishTime +
        ", startnumber: " +
        newManAddResult.startnumber
    );

    if (globalContext?.competition?.tag !== undefined) {
      const success = await PUT.resultPutPost(
        newManAddResult,
        globalContext.competition.tag
      );
      if (success) {
        console.log("Added result:", newManAddResult);
        await fetchResults(); // Refresh the list
      }
    } else {
      console.error("Ingen tävling är vald.");
    }
  };

  const mapToResultPostPutStartnumberDto = (
    updatedResult: Result
  ): ResultPostPutStartnumberDto => {
    return {
      startnumber: updatedResult.startnumber,
      finishTime: updatedResult.finishTime,
    };
  };

  return (
    <div>
      <VStack align="start" spacing={8}>
        <Text textAlign="left" fontSize="24" width="100%">
          Målgångslista ({filteredResults.length.toString()} av{" "}
          {results.length.toString()})
        </Text>
        <HStack
          padding={"10px"}
          spacing={4}
          justify={"space-evenly"}
          marginBottom={"30px"}
          marginTop={"-40px"}
        >
          <VStack>
            <HStack>
              <Button onClick={fetchResults} colorScheme="blue">
                Uppdatera lista
              </Button>
              <Button onClick={() => setIsModalOpen(true)}>
                Lägg till Löpare i målgångslistan
              </Button>
            </HStack>
            <AddResultModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onAddRow={addResultPostPut}
            />
          </VStack>

          <VStack>
            <Text>Välj klass</Text>
            <Select
              minW={100}
              placeholder="Alla"
              onChange={(event) => {
                if (filterClass === event.currentTarget.value) {
                  setFilterClass(undefined);
                } else {
                  setFilterClass(event.currentTarget.value);
                }
              }}
            >
              {globalContext.competition?.disciplines.map((c) => {
                return (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                );
              })}
            </Select>
          </VStack>
          <VStack>
            <Text>Sök på namn eller startnummer</Text>
            <Input
              minW={100}
              placeholder="Sök och hoppa till löpare"
              onChange={(event) => {
                if (searchStartnrOrName === event.currentTarget.value) {
                  setSearchStartnrOrName(undefined);
                } else {
                  setSearchStartnrOrName(event.currentTarget.value);
                }
              }}
            />
            <HStack width="100%" justify="space-between">
              <Button
                size="sm"
                onClick={() => {
                  setCurrentMatchIndex((old) =>
                    searchMatches.length === 0
                      ? 0
                      : (old - 1 + searchMatches.length) % searchMatches.length
                  );
                }}
                isDisabled={searchMatches.length <= 1}
              >
                Föregående
              </Button>
              <Text fontSize="sm" color="gray.700">
                {searchMatches.length === 0
                  ? "Ingen träff"
                  : `${currentMatchIndex + 1} / ${searchMatches.length}`}
              </Text>
              <Button
                size="sm"
                onClick={() => {
                  setCurrentMatchIndex((old) =>
                    searchMatches.length === 0
                      ? 0
                      : (old + 1) % searchMatches.length
                  );
                }}
                isDisabled={searchMatches.length <= 1}
              >
                Nästa
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.600">
              Listan filtreras inte, utan scrollar till första träffen.
            </Text>
          </VStack>
        </HStack>
      </VStack>

      <FastTable
        data={filteredResults}
        pageHeader={`Målgångslista till ${globalContext.competition?.tag ?? "Ingen tävling vald"} ${new Date().toISOString().split('T')[0]}`}
        repeatHeaderRows={3}
        downloadFileName={`MålgångslistaTill${globalContext.competition?.tag ?? "Ingen tävling vald"}_${new Date().toISOString().split('T')[0]}`}

        headers={[
          "Målgång",
          "Starttid",
          "Tid",
          "Startnr",
          "Klass",
          "Löpare",
          "Tid OK",
        ]}
        rowFormat={"3fr 3fr 3fr 1fr 1fr 8fr 1fr 1fr 1fr"}
        toRowData={(p) => {
          return [
            dateTimeInMsToTimeStringWithMs(p.finishTime),
            dateTimeInMsToTimeString(p.startTime),
            p.result,
            String(p.startnumber),
            p.disciplineName,
            `${p.firstname} ${p.lastname} (${p.birthYear})`,
            false,
          ];
        }}
        modifiableFields={modifyFields as unknown as Array<keyof Result>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        fixedFields={fixedFields as Array<keyof Result>}
        fixedFieldsDisplayName={fixedDisplayLabels}
        focusedRow={focusedResult}
        onChange={async (updatedResult) => {
          const okChange = validDateTimeStr(`${updatedResult.goalTime}`);

          if (okChange) {
            updatedResult.finishTime = dateTimeStringToMs(
              updatedResult.goalTime
            );
            updatedResult.resultInMs =
              updatedResult.startTime !== null &&
              updatedResult.finishTime !== null
                ? updatedResult.finishTime - updatedResult.startTime
                : null;
            updatedResult.result = timeInMsToStringUTC(
              updatedResult.resultInMs
            );
            const dto = mapToResultPostPutStartnumberDto(updatedResult);
            if (globalContext?.competition?.tag !== undefined) {
              const success = await PUT.resultPutPost(
                dto,
                globalContext.competition.tag
              );
              if (success) {
                setResults((old) => {
                  return old.map((p) =>
                    p.id === updatedResult.id ? updatedResult : p
                  );
                });
              }
            } else {
              console.error("Ingen tävling är vald.");
            }
            return true;
          } else {
            return false;
          }
        }}
        onDelete={async (deletedResult) => {
          if (disableModifying) {
            return true;
          }
          console.info(
            "Delete: " +
              deletedResult.id +
              ", " +
              deletedResult.startnumber +
              ", " +
              deletedResult.firstname
          );
          const success = await DELETE.result(deletedResult);
          if (success) {
            await fetchResults(); // Refresh the list
          }
          return success;
        }}
        onReorder={async (dragged, droppedOn) => {
          if (!globalContext?.competition?.tag) return;
          const newDraggedResultInMs =
            dragged.startTime !== null && droppedOn.finishTime !== null
              ? droppedOn.finishTime - dragged.startTime
              : null;
          const newDragged: DisplayResult = {
            ...dragged,
            finishTime: droppedOn.finishTime,
            goalTime: droppedOn.finishTime
              ? dateTimeInMsToStringWithMs(droppedOn.finishTime)
              : "-",
            resultInMs: newDraggedResultInMs,
            result: newDraggedResultInMs
              ? timeInMsToStringUTC(newDraggedResultInMs)
              : "-",
          };
          const newDroppedOnResultInMs =
            droppedOn.startTime !== null && dragged.finishTime !== null
              ? dragged.finishTime - droppedOn.startTime
              : null;
          const newDroppedOn: DisplayResult = {
            ...droppedOn,
            finishTime: dragged.finishTime,
            goalTime: dragged.finishTime
              ? dateTimeInMsToStringWithMs(dragged.finishTime)
              : "-",
            resultInMs: newDroppedOnResultInMs,
            result: newDroppedOnResultInMs
              ? timeInMsToStringUTC(newDroppedOnResultInMs)
              : "-",
          };
          await Promise.all([
            PUT.resultPutPost(
              {
                startnumber: newDragged.startnumber,
                finishTime: newDragged.finishTime,
              },
              globalContext.competition.tag
            ),
            PUT.resultPutPost(
              {
                startnumber: newDroppedOn.startnumber,
                finishTime: newDroppedOn.finishTime,
              },
              globalContext.competition.tag
            ),
          ]);
          setResults((old) =>
            old.map((p) => {
              if (p.id === dragged.id) return newDragged;
              if (p.id === droppedOn.id) return newDroppedOn;
              return p;
            })
          );
        }}
      />
    </div>
  );
}

export default FinishList;
